import type { JSX } from "react";
// Number format context.
//
// Controls how numbers and money are rendered across the app:
//   - "US" → 1,234,567.89 (en-US locale)
//   - "EU" → 1.234.567,89 (de-DE locale)
//
// Persisted in localStorage under `preferred_number_format`. Default is US.
// Money formatting still uses Intl currency style — only the locale changes.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

export const NUMBER_FORMATS = ["US", "EU"] as const;
export type NumberFormat = (typeof NUMBER_FORMATS)[number];

const STORAGE_KEY = "preferred_number_format";
const DEFAULT_FORMAT: NumberFormat = "US";

const LOCALE: Record<NumberFormat, string> = {
  US: "en-US",
  EU: "de-DE",
};

interface NumberFormatContextValue {
  format: NumberFormat;
  setFormat: (f: NumberFormat) => void;
  locale: string;
  formatNumber: (n: number, opts?: Intl.NumberFormatOptions) => string;
  /** amountMinor → "$1,234" / "1.234 €" depending on locale + currency.
   *  Defaults to whole units (0 decimals); pass `{ minimumFractionDigits: 2,
   *  maximumFractionDigits: 2 }` where cents matter (e.g. transactions). */
  formatMoney: (
    amountMinor: number | string | bigint,
    currency: string,
    opts?: Intl.NumberFormatOptions,
  ) => string;
}

const NumberFormatContext = createContext<NumberFormatContextValue | null>(null);

function readStored(): NumberFormat {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v && (NUMBER_FORMATS as readonly string[]).includes(v)) return v as NumberFormat;
  } catch { /* ignore */ }
  return DEFAULT_FORMAT;
}

export function NumberFormatProvider({ children }: { children: ReactNode }): JSX.Element {
  const [format, setFormatState] = useState<NumberFormat>(readStored);

  const setFormat = useCallback((f: NumberFormat) => {
    setFormatState(f);
    try { window.localStorage.setItem(STORAGE_KEY, f); } catch { /* ignore */ }
  }, []);

  const locale = LOCALE[format];

  const formatNumber = useCallback(
    (n: number, opts?: Intl.NumberFormatOptions) => new Intl.NumberFormat(locale, opts).format(n),
    [locale],
  );

  const formatMoney = useCallback(
    (amountMinor: number | string | bigint, currency: string, opts?: Intl.NumberFormatOptions) => {
      const n = typeof amountMinor === "bigint" ? Number(amountMinor) : Number(amountMinor);
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
        ...opts,
      }).format(n / 100);
    },
    [locale],
  );

  const value = useMemo<NumberFormatContextValue>(
    () => ({ format, setFormat, locale, formatNumber, formatMoney }),
    [format, setFormat, locale, formatNumber, formatMoney],
  );

  return <NumberFormatContext.Provider value={value}>{children}</NumberFormatContext.Provider>;
}

export function useNumberFormat(): NumberFormatContextValue {
  const ctx = useContext(NumberFormatContext);
  if (!ctx) throw new Error("useNumberFormat must be used inside <NumberFormatProvider>");
  return ctx;
}
